import { defineStore } from 'pinia'
import { camelCase } from 'lodash'
import camelcase from 'camelcase'
import repositoryFactory from '@/factories/repository-factory'
import initDataRepository from '@/repositories/init-data-repository'
import InitData, { StationListItem, ThresholdInfo } from '@/models/InitData'
import { LayoutInfoIcon, LayoutInfoMenuIcon } from '@/models/initData/LayoutInfo'
import Area from '@/models/Area'
import Station from '@/models/Station'
import { TopViewType } from '@/stores/globalStore'

const storeKey = 'initData'

// フィールド定義未定義分
// 定義され次第API経由に変更
const TEMP_FIELD_LIST = {
  moveWdMin10: 'm/s',
  moveWsMin10: 'm/s',
  temp: '℃',
  humid1: '%',
  press1: 'hPa',
  rainFall: 'mm/h',
}

export type InitDataStore = {
  initData: InitData
  loading: boolean
}

export const useInitDataStore = defineStore(storeKey, {
  state: () =>
    ({
      loading: true,
      initData: {
        transactId: undefined,
        systemInfo: {
          title: '',
          mapInfo: {},
          favicon: '',
        },
        layoutInfo: {
          top: {
            graphical: {
              launcher: {
                left: {
                  bottom: [] as LayoutInfoIcon[],
                  top: [] as LayoutInfoIcon[],
                },
              },
            },
            summary: {
              launcher: {
                left: {
                  bottom: [] as LayoutInfoIcon[],
                  top: [] as LayoutInfoIcon[],
                },
              },
            },
          },
          header: {
            menu: [] as LayoutInfoMenuIcon[],
          },
        },
        definitionInfo: {
          top: {
            summary: {},
          },
          reports: {
            common: {},
          },
          earthquake: {},
          link: {},
        },
        stationList: [] as StationListItem[],
        thresholdInfo: {
          colorInfo: {},
        } as ThresholdInfo,
      },
    } as InitDataStore),

  getters: {
    findIconInfo: state => (fieldId: string) => {
      const key = camelCase(fieldId)

      return state.initData.iconInfo[key].items
    },

    decimal: state => (fieldId: string) => {
      return state.initData.fieldList[fieldId].decimal
    },

    definitionInfoLink: state => {
      const tabs = state.initData.definitionInfo?.link?.tabs || []
      const tab = tabs[0] || {}

      return tab.items || []
    },

    definitionInfoTop: state => (summaryId?: string) => {
      return state.initData.definitionInfo?.top.summary[summaryId || 'topSummary1']
    },

    definitionInfoReports: state => {
      return state.initData.definitionInfo?.reports.common
    },

    definitionInfoEarthquake: state => {
      return state.initData.definitionInfo?.earthquake
    },

    definitionInfoRain: state => (resolution: string) => {
      const rainInfo = state.initData.definitionInfo?.reports.rain

      if (!rainInfo) {
        return
      }

      return rainInfo[resolution]
    },

    definitionInfoWater: state => (resolution: string) => {
      const waterInfo = state.initData.definitionInfo?.reports.water

      if (!waterInfo) {
        return
      }
      // TODO: minuteプロパティが受け取れるようになったら削除
      waterInfo['minute'] = { ...waterInfo['hour'] }

      return waterInfo[resolution]
    },

    definitionInfoWeather: state => (resolution: string) => {
      const rainInfo = state.initData.definitionInfo?.reports.weather

      if (!rainInfo) {
        return
      }

      return rainInfo[resolution]
    },

    leftLauncherInfo: state => (viewType: TopViewType) => {
      return state.initData.layoutInfo.top[viewType].launcher.left
    },

    areaList: state => {
      return state.initData?.areaList?.map(a => new Area(a))
    },

    findArea: state => (id: string) => {
      const area = state.initData?.areaList?.find(a => a.id === id)

      if (area) {
        return new Area(area)
      }
    },

    findStation: state => (id: string) => {
      const station = state.initData?.stationList?.find(s => s.stationId === id)

      if (station) {
        return new Station(station)
      }
    },

    fieldListItem: state => (name: string) => {
      const fieldList = state.initData.fieldList

      return fieldList[name]
    },

    mapInfo: state => {
      return state.initData.systemInfo.mapInfo
    },

    headerMenus: state => {
      return state.initData.layoutInfo.header.menu
    },

    getUnit: state => (elementId: string) => {
      if (!state.initData) {
        return ''
      }

      const key = camelcase(elementId)
      const field = state.initData.fieldList[key]

      return field?.unit || TEMP_FIELD_LIST[key as keyof typeof TEMP_FIELD_LIST]
    },

    getDecimal: state => (elementId: string) => {
      if (!state.initData) {
        return 0
      }

      return state.initData.fieldList[camelcase(elementId)].decimal
    },

    thresholdColorInfo: state => {
      const colorInfo = state.initData.thresholdInfo?.colorInfo

      if (colorInfo === undefined) {
        return
      }

      const results = {} as { [key: string]: string }

      Object.keys(colorInfo).forEach(key => {
        const color = colorInfo[key].color

        results[key] = color
      })

      return results
    },
  },

  actions: {
    async fetch() {
      const repository = (await repositoryFactory.create()).get(storeKey) as initDataRepository

      this.initData = await repository.get()
      this.loading = false
    },
  },
})
